import React from 'react';

interface ServiceCost {
  service: string;
  cost: number;
  percentage: number;
}

interface CostByServiceProps {
  services: ServiceCost[];
  currency?: string;
}

/**
 * Cost by Service Component
 * Displays cost breakdown by cloud services (EC2, S3, RDS, etc.)
 */
export const CostByService: React.FC<CostByServiceProps> = ({ services, currency = 'USD' }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (!services || services.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
        <h3 className="text-lg font-semibold text-foreground">Cost by Service</h3>
        <p className="mt-4 text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  // Sort by cost descending
  const sortedServices = [...services].sort((a, b) => b.cost - a.cost);
  const topServices = sortedServices.slice(0, 10); // Show top 10

  return (
    <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
      <h3 className="text-lg font-semibold text-foreground">Cost by Service</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Top {topServices.length} services by cost
      </p>

      <div className="mt-6 space-y-4">
        {topServices.map((service, index) => (
          <div key={service.service} className="flex items-center gap-4">
            {/* Rank */}
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
              {index + 1}
            </div>

            {/* Service Name */}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">
                {service.service}
              </div>
              {/* Progress Bar */}
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-emerald-600 transition-all duration-300"
                  style={{ width: `${service.percentage}%` }}
                />
              </div>
            </div>

            {/* Cost and Percentage */}
            <div className="flex flex-col items-end">
              <div className="text-sm font-semibold text-foreground">
                {formatCurrency(service.cost)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatPercentage(service.percentage)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedServices.length > 10 && (
        <div className="mt-4 text-center text-sm text-muted-foreground">
          +{sortedServices.length - 10} more services
        </div>
      )}
    </div>
  );
};
